// Les briques de la candidature, partagées par les trois surfaces qui la
// manipulent : la pile de décisions, le suivi et la fiche offre.
//
// Elles vivent ici et pas dans une page parce que c'est exactement le genre de
// composant que ce dépôt a déjà vu diverger (cf. les boutons recopiés de chaque
// côté, puis les seuils de score) : trois écrans qui affichent la même étape
// avec trois couleurs, et rien pour le signaler.

import { useEffect, useRef, useState } from 'react'
import { Check, ChevronDown, UserPlus } from 'lucide-react'
import { mouvementReduit } from '@ai-job-fit/ui'
import { post, type CandidatureStatus, type ConsultantRow } from '../lib/api'
import { CANDIDATURE_STATUS, initials, teinte } from '../lib/format'
import { ActionButton, useToast } from './common'
import { useProfilUnique } from '../lib/formule'
import { useCandidatureStatus } from '../lib/dictionnaires'
import { useT } from '../lib/i18n'
import { useMots } from '../lib/vocabulaire'

/** L'ordre du pipeline, dérivé du dictionnaire de libellés : une seule source. */
// L'ORDRE du pipeline, qui ne dépend PAS de la formule : ce sont les mêmes
// étapes des deux côtés, seuls leurs libellés changent. Le lire sur le
// dictionnaire console est donc exact, et pas un raccourci.
export const ETAPES = Object.keys(CANDIDATURE_STATUS) as CandidatureStatus[]

export function EtapeChip({ status, size }: { status: CandidatureStatus; size?: 'sm' }) {
  const ETAT = useCandidatureStatus()
  const e = ETAT[status]
  return (
    <span className={`kchip ${e.cls}${size === 'sm' ? ' sm' : ''}`} title={e.aide}>
      <span className="kd" />
      {e.label}
    </span>
  )
}

/**
 * Changer l'étape d'une candidature.
 *
 * Un menu et non un enchaînement « étape suivante » : un dossier ne progresse
 * pas toujours d'un cran (on passe d'« à envoyer » à « refusé » quand le client
 * a pourvu le poste ailleurs), et un bouton unique obligerait à traverser trois
 * états faux pour arriver au bon.
 */
// Le dernier statut choisi ici (#185). La ligne change souvent de groupe en
// changeant de statut, donc le composant est remonté de zéro : c'est à son
// arrivée dans le nouveau groupe que la pastille doit rebondir.
let dernierChoix: { id: number; at: number } | null = null

export function SelecteurEtape({
  candidatureId,
  status,
  onChanged,
}: {
  candidatureId: number
  status: CandidatureStatus
  onChanged: () => void
}) {
  const ETAT = useCandidatureStatus()
  const t = useT()
  const [ouvert, setOuvert] = useState(false)
  const boite = useRef<HTMLDivElement>(null)
  const toast = useToast()
  // Né du choix qu'on vient de faire (ligne remontée dans son nouveau
  // groupe) : c'est ce composant-ci qui consomme la note, jamais l'ancien,
  // qui la lirait avant que la liste rechargée ne le remplace.
  const neDuChoix = useRef(!!dernierChoix && dernierChoix.id === candidatureId && Date.now() - dernierChoix.at < 15000)
  const [vient, setVient] = useState(() => neDuChoix.current && !mouvementReduit())
  useEffect(() => {
    if (neDuChoix.current) { dernierChoix = null; neDuChoix.current = false }
    if (!vient) return
    const fin = setTimeout(() => setVient(false), 800)
    return () => clearTimeout(fin)
  }, [vient])

  // Fermeture au clic extérieur ET à Échap : sans le second, le menu reste
  // ouvert derrière le clavier et piège la tabulation.
  useEffect(() => {
    if (!ouvert) return
    const dehors = (e: MouseEvent) => {
      if (!boite.current?.contains(e.target as Node)) setOuvert(false)
    }
    const echap = (e: KeyboardEvent) => { if (e.key === 'Escape') setOuvert(false) }
    document.addEventListener('mousedown', dehors)
    document.addEventListener('keydown', echap)
    return () => {
      document.removeEventListener('mousedown', dehors)
      document.removeEventListener('keydown', echap)
    }
  }, [ouvert])

  async function choisir(s: CandidatureStatus) {
    setOuvert(false)
    if (s === status) return
    await post(`/api/candidatures/${candidatureId}/status`, { status: s })
    dernierChoix = { id: candidatureId, at: Date.now() }
    if (!mouvementReduit()) setVient(true)
    toast(t('candidature.stepToast', { label: ETAT[s].label }))
    onChanged()
  }

  return (
    <div className="kpick" ref={boite}>
      <button
        className={`kpick-btn${vient ? ' vient' : ''}`}
        onClick={(e) => { e.stopPropagation(); setOuvert((v) => !v) }}
        aria-haspopup="listbox"
        aria-expanded={ouvert}
      >
        <EtapeChip status={status} />
        <ChevronDown size={13} />
      </button>
      {ouvert && (
        <div className="kmenu" role="listbox" onClick={(e) => e.stopPropagation()}>
          {ETAPES.map((s) => (
            <button
              key={s}
              role="option"
              aria-selected={s === status}
              className={`kmenu-i${s === status ? ' on' : ''}`}
              onClick={() => choisir(s)}
            >
              <span className={`kd ${ETAT[s].cls}`} />
              <span className="grow">
                <b>{ETAT[s].label}</b>
                <span className="kmenu-h">{ETAT[s].aide}</span>
              </span>
              {s === status && <Check size={13} />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Proposer un profil sur une offre — le geste qui fait sortir une offre de la
 * pile de décisions.
 *
 * Il ne coûte RIEN : ni appel IA, ni document. C'est tout l'objet du modèle à
 * deux niveaux — avant, le seul moyen pour un consultant d'exister sur une
 * offre était de payer la génération d'un CV.
 */
export function ProposerProfil({
  offerId,
  profils,
  onDone,
  variant = 'secondary',
  compact,
}: {
  offerId: number
  /** Les profils proposables, du meilleur au moins bon. */
  profils: { consultantId: number; name: string; label: string; score: number | null }[]
  onDone: () => void
  variant?: 'primary' | 'secondary'
  /**
   * Libellé de largeur constante, pour les listes.
   *
   * « Proposer Nicolas » est plus large que « Proposer » : dans une liste dense
   * les lignes à un seul profil avaient donc un bloc d'actions plus large, qui
   * décalait l'entreprise et la date de ces lignes-là. Une colonne qui ondule
   * ruine la lecture verticale, seule raison d'être de cette mise en page.
   */
  compact?: boolean
}) {
  const profilUnique = useProfilUnique()
  const t = useT()
  const mots = useMots()
  const [ouvert, setOuvert] = useState(false)
  const boite = useRef<HTMLDivElement>(null)
  const toast = useToast()

  useEffect(() => {
    if (!ouvert) return
    const dehors = (e: MouseEvent) => {
      if (!boite.current?.contains(e.target as Node)) setOuvert(false)
    }
    const echap = (e: KeyboardEvent) => { if (e.key === 'Escape') setOuvert(false) }
    document.addEventListener('mousedown', dehors)
    document.addEventListener('keydown', echap)
    return () => {
      document.removeEventListener('mousedown', dehors)
      document.removeEventListener('keydown', echap)
    }
  }, [ouvert])

  if (profils.length === 0) return null

  async function proposer(consultantId: number, nom: string) {
    setOuvert(false)
    await post(`/api/offers/${offerId}/candidatures`, { consultantId })
    toast(profilUnique ? t('offre.appliedToast') : t('candidature.proposedToast', { name: nom }))
    onDone()
  }

  // Un seul profil évalué : pas de menu, le clic suffit. Ouvrir une liste d'un
  // élément est un geste de plus pour zéro information.
  if (profils.length === 1) {
    const p = profils[0]
    // « Proposer Amélie » adressé à Amélie n'a pas de sens : on ne se propose
    // pas soi-même, on postule. Et son prénom dans le bouton est du langage de
    // tiers, celui qu'on emploie pour parler de quelqu'un à quelqu'un d'autre.
    const libelle = profilUnique
      ? mots('etat.postuler')
      : compact
        ? t('candidature.propose')
        : t('candidature.proposeName', { name: p.name.split(' ')[0] })
    return (
      <ActionButton
        size="sm"
        variant={variant}
        title={compact && !profilUnique ? t('candidature.proposeName', { name: p.name }) : undefined}
        onClick={() => proposer(p.consultantId, p.name)}
      >
        <UserPlus size={13} />{libelle}
      </ActionButton>
    )
  }

  return (
    <div className="kpick" ref={boite}>
      <button
        className={variant === 'primary' ? 'btn-primary btn-sm' : 'btn-secondary btn-sm'}
        onClick={(e) => { e.stopPropagation(); setOuvert((v) => !v) }}
        aria-haspopup="listbox"
        aria-expanded={ouvert}
      >
        <UserPlus size={13} />{t('candidature.propose')}<ChevronDown size={13} />
      </button>
      {ouvert && (
        <div className="kmenu wide" role="listbox" onClick={(e) => e.stopPropagation()}>
          {profils.map((p) => (
            <button
              key={p.consultantId}
              role="option"
              aria-selected={false}
              className="kmenu-i"
              onClick={() => proposer(p.consultantId, p.name)}
            >
              <span className="miniav" style={teinte(p.name)}>{initials(p.name)}</span>
              <span className="grow">
                <b>{p.name}</b>
                <span className="kmenu-h">{p.label}</span>
              </span>
              {p.score !== null && <span className="kscore">{p.score}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/** Le vivier réduit aux profils réellement proposables. */
export const disponibles = (vivier: ConsultantRow[] | null): ConsultantRow[] =>
  (vivier ?? []).filter((c) => c.status === 'available')
